/**
 * Deterministic fixed-point math for movement and tracking.
 * 16.16 fixed-point stored in a JS number; intermediates use BigInt so every
 * operation is exact integer math - bit-identical on every engine. No Math
 * global (repo lint ban), no floats in arithmetic, no transcendentals.
 */

export type Fx = number; // value * 2^16

export const FX_SHIFT = 16;
export const FX_ONE: Fx = 65536;

export function fxFromInt(v: number): Fx {
  if (!Number.isInteger(v)) throw new Error('fxFromInt: not an integer');
  return v * FX_ONE;
}

export function fxToInt(v: Fx): number {
  // Truncation toward zero, matching | 0 on the scaled integer.
  return (v / FX_ONE) | 0;
}

/** Round-half-down to the nearest integer. */
export function fxRound(v: Fx): number {
  return ((v + FX_ONE / 2) / FX_ONE) | 0;
}

export function fxMul(a: Fx, b: Fx): Fx {
  return Number((BigInt(a) * BigInt(b)) >> BigInt(FX_SHIFT));
}

export function fxDiv(a: Fx, b: Fx): Fx {
  if (b === 0) throw new Error('fxDiv: divide by zero');
  return Number((BigInt(a) << BigInt(FX_SHIFT)) / BigInt(b));
}

export function fxAbs(a: Fx): Fx { return a < 0 ? -a : a; }

export function fxClamp(v: Fx, lo: Fx, hi: Fx): Fx {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Integer square root via Newton's method on BigInt: floor(sqrt(n)).
 * Deterministic, no Math.sqrt. Used for distances and vector normalization.
 */
export function isqrt(n: number): number {
  if (n < 0) throw new Error('isqrt: negative');
  return isqrtBig(BigInt(n));
}

/** BigInt variant for sums of squares that exceed 2^53. */
export function isqrtBig(n: bigint): number {
  if (n < 2n) return Number(n);
  let x = n;
  let y = (n + 1n) / 2n;
  while (y < x) {
    x = y;
    y = (x + n / x) / 2n;
  }
  return Number(x);
}

/** Fixed-point vector length: floor(sqrt(x^2 + y^2)) on unshifted BigInt sum. */
export function fxLength(x: Fx, y: Fx): Fx {
  return isqrtBig(BigInt(x) * BigInt(x) + BigInt(y) * BigInt(y));
}
